import type { TableProps } from '@aibees/shared/components/table';
import { useSearchParamsSource } from '@aibees/shared/hooks';
import { toNumber } from '@aibees/shared/utils';
import { isEqual } from 'lodash-es';
import { useEffect, useRef } from 'react';
import {
  useAdvanceTableConfigContext,
  useAdvanceTableDataFiltersContext,
} from '../../../context';

export function useApplyTableFiltersToQueryParams(table: TableProps) {
  const { config } = useAdvanceTableConfigContext();
  const { filters } = useAdvanceTableDataFiltersContext();
  const { params = {}, setParam, setParams } = useSearchParamsSource();
  const prevFiltersRef = useRef<typeof filters | null>(null);

  useEffect(() => {
    if (
      (toNumber(params.page) !== table.page && table.page !== 0) ||
      (table.page === 0 && !!params.page)
    ) {
      setParam('page', table.page.toString());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table.page]);

  useEffect(() => {
    if (toNumber(params.size) !== table.rowsPerPage && !!params.size)
      setParam('size', table.rowsPerPage.toString());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table.rowsPerPage]);

  useEffect(() => {
    if (!config.applyStateToQueryParams) return;
    if (isEqual(filters, prevFiltersRef.current)) return;
    const nextParams = { ...filters };
    // Normalize page/size to string so isEqual matches store shape and we avoid redundant setParams
    if ('page' in nextParams && nextParams.page != null)
      nextParams.page = String(nextParams.page);
    if ('size' in nextParams && nextParams.size != null)
      nextParams.size = String(nextParams.size);
    if (isEqual(params, nextParams)) {
      prevFiltersRef.current = filters;
      return;
    }
    prevFiltersRef.current = filters;
    setParams(nextParams);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.applyStateToQueryParams, filters]);
}
